import { RadioButtonGroup, RadioButton, Button } from '@carbon/react';
import { Edit, TrashCan, Locked } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useCallback } from 'react';
import { debounce } from 'lodash-es';
import { MapRef } from 'react-map-gl';

import {
  spatialFilterModeAtom,
  aoiPolygonAtom,
  aoiPolygonLockedAtom,
  broadcastSearchUpdateAtom,
  mapSearchRefAtom,
  type MapMoveMeta,
} from '../atoms';
import type { SpatialFilterMode } from '../types';
import { FilterChip } from '../filter-chip';
import styles from './spatial-filter-controls.module.scss';

export const SpatialFilterControls = () => {
  const [spatialMode, setSpatialMode] = useAtom(spatialFilterModeAtom);
  const broadcastUpdate = useSetAtom(broadcastSearchUpdateAtom);
  const mapRef = useAtomValue(mapSearchRefAtom);
  const setAoiPolygon = useSetAtom(aoiPolygonAtom);
  const aoiPolygon = useAtomValue(aoiPolygonAtom);
  const [aoiLocked, setAoiLocked] = useAtom(aoiPolygonLockedAtom);

  const updateBoundsFromMap = useMemo(
    () =>
      debounce((map: MapRef) => {
        const bounds = map.getBounds();
        if (!bounds) return;
        broadcastUpdate((current) => ({
          ...current,
          page: 1,
          bounding_box: {
            sw_lng: bounds.getWest(),
            sw_lat: bounds.getSouth(),
            ne_lng: bounds.getEast(),
            ne_lat: bounds.getNorth(),
          },
          polygon_geojson: undefined,
        }));
      }, 500),
    [broadcastUpdate]
  );

  // Attach/detach moveend listener based on spatial mode
  useEffect(() => {
    if (spatialMode === 'visible-extent' && mapRef) {
      // Skip re-search for moves we trigger ourselves (e.g. flying to a
      // selected card), so the fly doesn't rebound the search and drop it.
      const handler = (e?: MapMoveMeta) => {
        if (e?.suppressBoundsSearch) return;
        updateBoundsFromMap(mapRef);
      };
      // A suppressed fly also cancels a bounds search still pending from an
      // earlier user pan. movestart fires as the fly begins — before that
      // debounce could run on the flown-to viewport and drop the selection —
      // whereas moveend fires only when the fly ends, often past the debounce.
      const cancelPendingOnSuppressedMove = (e?: MapMoveMeta) => {
        if (e?.suppressBoundsSearch) updateBoundsFromMap.cancel();
      };
      mapRef.on('movestart', cancelPendingOnSuppressedMove);
      mapRef.on('moveend', handler);
      // Trigger initial bounds search
      handler();
      // React runs this cleanup before re-running the effect (mode/map change)
      // and on unmount, so it also tears the listeners down when switching away.
      return () => {
        mapRef.off('movestart', cancelPendingOnSuppressedMove);
        mapRef.off('moveend', handler);
      };
    }
    return undefined;
  }, [spatialMode, mapRef, updateBoundsFromMap]);

  const handleChange = (value: string | number | undefined) => {
    const mode = String(value) as SpatialFilterMode;
    setSpatialMode(mode);

    if (mode === 'none') {
      // Clear spatial filters across all datasets
      broadcastUpdate((current) => ({
        ...current,
        page: 1,
        bounding_box: undefined,
        polygon_geojson: undefined,
      }));
      setAoiPolygon(null);
      setAoiLocked(true);
    }

    // When entering aoi-polygon mode, do NOT broadcast an update.
    // The draw control will broadcast with polygon_geojson once the user
    // finishes drawing. Clearing bounding_box here would trigger a
    // premature query with no spatial filter.
  };

  const handleEditPolygon = useCallback(() => {
    setAoiLocked(false);
  }, [setAoiLocked]);

  const handleLockPolygon = useCallback(() => {
    setAoiLocked(true);
  }, [setAoiLocked]);

  const handleDeletePolygon = useCallback(() => {
    setAoiPolygon(null);
    setAoiLocked(true);
    broadcastUpdate((current) => ({
      ...current,
      page: 1,
      polygon_geojson: undefined,
    }));
  }, [setAoiPolygon, setAoiLocked, broadcastUpdate]);

  return (
    <div>
      <RadioButtonGroup
        legendText=""
        name="spatial-filter"
        valueSelected={spatialMode}
        onChange={handleChange}
        orientation="vertical"
      >
        <RadioButton id="spatial-none" labelText="No Map Filter" value="none" />
        <RadioButton
          id="spatial-extent"
          labelText="Use Visible Map Extent"
          value="visible-extent"
        />
        <RadioButton
          id="spatial-polygon"
          labelText="Draw AOI Polygon"
          value="aoi-polygon"
        />
      </RadioButtonGroup>
      {spatialMode === 'aoi-polygon' && aoiPolygon && (
        <div className={styles.polygonGroup}>
          <div className={styles.polygonHeader}>
            <span className={styles.polygonName}>AOI Polygon</span>
            {aoiLocked ? (
              <>
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={Edit}
                  iconDescription="Edit polygon"
                  tooltipPosition="bottom"
                  onClick={handleEditPolygon}
                />
                <Button
                  kind="danger--ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Delete polygon"
                  tooltipPosition="bottom"
                  onClick={handleDeletePolygon}
                />
              </>
            ) : (
              <Button
                kind="primary"
                size="sm"
                renderIcon={Locked}
                onClick={handleLockPolygon}
              >
                Done
              </Button>
            )}
          </div>
          <div className={styles.chipList}>
            {aoiLocked ? (
              <FilterChip
                onClose={handleDeletePolygon}
                removeLabel="Remove polygon filter"
              >
                Polygon drawn
              </FilterChip>
            ) : (
              <span className={styles.editingHint}>
                Edit vertices on the map, then click Done
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
